// Author    : Dimitri Sabadie <dimitri.sabadie@sculpteo.com>
// Copyright : (C) Sculpteo 2015
//
// This file exports the concept of picking. It dynamically adapts to the number of objects to pick
// so that the implementation used behind the hood suits the best. Basically, if you have less than
// 256 (excluded) objects to pick, a fast stencil-based method is used. If you have more, all you're
// objects are rendered another time.
//
// The idea is to use that object at two different places: while rendering the depthmap and while
// rendering your index map. The index map contains the identifiers you'll be able to use later on
// to identify your objects.
//
// Before rendering your depthmap, call the function 'beforeDepthMapRender' before the actual render
// loop. That function is used to prepare the picking and optimize it.
//
// When rendering your depthmap, call the function 'whileDepthMapRender' of any object you want to
// pick later.
//
// Then use the function 'runPicking' to run the picking process. You can then use the
// 'indexAt' function to retrieve the index at the given location on your surface of the screen (the
// cursor mouse, for instance).

var Sculpteo = (function (module) {
  // Picking type.
  module.Picking = function (width, height, viewer) {
    var gl = viewer.gl;

    // Index buffer; used to retrieve the indices from OpenGL to JS
    this.index_buffer = new Uint8Array(4 * width * height);

    // Index map.
    var _indexMap = gl.createTexture();
    _indexMap.width = width;
    _indexMap.height = height;
    gl.bindTexture(gl.TEXTURE_2D, _indexMap);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      width,
      height,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      null
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    // This function should be called before rendering into your depthmap to prepare OpenGL to
    // perform picking.
    //
    // The 'n' parameter is the number of objects you intend to pick and thus uniquely identify.
    this.beforeDepthMapRender = function (n) {
      if (n < 256) {
        gl.clearStencil(0);
        gl.clear(gl.STENCIL_BUFFER_BIT);
        gl.enable(gl.STENCIL_TEST);
        gl.stencilFunc(gl.ALWAYS, 0x01, 0xff);
        gl.stencilMask(0xff);
        gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
      }
    };

    // This function should be called while you're rendering the depthmap. That function there to
    // optimize the picking process when you have less than 256 objects to render.
    //
    // The 'n' parameter is the number of objects you intend to pick and thus uniquely identify.
    this.whileDepthMapRender = function (n, index) {
      if (n < 256) {
        gl.stencilFunc(gl.ALWAYS, 0x01 + index, 0xff);
      }
    };

    // This function should be called after the depth map has been rendered.
    this.afterDepthMapRender = function (n) {
      if (n < 256) {
        gl.disable(gl.STENCIL_TEST);
      }
    };

    // Run the picking process.
    //
    // You need to call that function to update the picking so that you can get the index at a given
    // location. See the function 'indexAt' for further details.
    //
    // Notice: if you want to perform picking with CSG renders, please use the 'runPickingCSG'
    // function.
    this.runPicking = function (nodes) {
      gl.useProgram(viewer._programConstantColor);

      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        _indexMap,
        0
      );

      gl.enableVertexAttribArray(viewer._programConstantColor.vertexAttribute);

      gl.clearColor(0.0, 0.0, 0.0, 0.0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.colorMask(true, true, true, true);

      // If we have less than 256 nodes, we can render the node map directly by copying the stencil
      // buffer to the color buffer; otherwise, we need to render all nodes once again to alter the
      // index map
      if (nodes.length < 256) {
        if (
          typeof viewer._programConstantColor.projectionMatrix != "undefined"
        ) {
          gl.uniformMatrix4fv(
            viewer._programConstantColor.projectionMatrix,
            false,
            viewer.identity
          );
        }

        if (
          typeof viewer._programConstantColor.modelViewMatrix != "undefined"
        ) {
          gl.uniformMatrix4fv(
            viewer._programConstantColor.modelViewMatrix,
            false,
            viewer.identity
          );
        }

        gl.disable(gl.DEPTH_TEST);
        gl.depthMask(false);
        gl.stencilMask(0xff);
        gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
        gl.enable(gl.STENCIL_TEST);

        // Draw a quad for each node to copy stencil to color buffer
        for (var n = 0; n < nodes.length; n++) {
          var node = nodes[n];

          if (typeof node == "undefined") {
            continue;
          }

          if (
            typeof viewer.cursorInstance != "undefined" &&
            node.instance.name == viewer.cursorInstance.instance.name
          ) {
            continue;
          }

          if (!(node.drawmode & Sculpteo3DViewerCommon.DRAWMODE_FLAT)) {
            continue;
          }

          if (!viewer.getActiveModel(node)) {
            continue;
          }

          gl.stencilFunc(gl.EQUAL, 0x01 + n, 0xff);

          gl.uniform4f(
            viewer._programConstantColor.color,
            0.0,
            0.0,
            0.0,
            n / 255.0
          );

          viewer.drawQuad(viewer._programConstantColor, 1.0);
        }

        gl.enable(gl.DEPTH_TEST);
        gl.disable(gl.STENCIL_TEST);
        gl.depthMask(true);
      } else {
        var pMatrix = viewer.pMatrix
          .scale(1.0, 1.0, 0.5)
          .translate(0.0, 0.0, 0.5);

        gl.uniformMatrix4fv(
          viewer._programConstantColor.projectionMatrix,
          false,
          pMatrix.toFloat32Array()
        );

        for (var n = 0; n < nodes.length; n++) {
          var node = nodes[n];

          if (typeof node == "undefined") {
            continue;
          }

          if (
            typeof viewer.cursorInstance != "undefined" &&
            node.instance.name == viewer.cursorInstance.instance.name
          ) {
            continue;
          }

          if (!(node.drawmode & Sculpteo3DViewerCommon.DRAWMODE_FLAT)) {
            continue;
          }

          var vbos = viewer.getActiveModel(node);

          if (!vbos) {
            continue;
          }

          var mvMatrix = viewer.mvMatrix.x(node.transform);

          gl.uniformMatrix4fv(
            viewer._programConstantColor.modelViewMatrix,
            false,
            mvMatrix.toFloat32Array()
          );

          var rgbaNode = packRGBA8(n); // Convert the node's index to an RGBA colour

          gl.uniform4f(
            viewer._programConstantColor.color,
            rgbaNode[0] / 255,
            rgbaNode[1] / 255,
            rgbaNode[2] / 255,
            rgbaNode[3] / 255
          );

          viewer.drawModelWithPositionsAndNormals(
            vbos,
            viewer._programConstantColor.vertexAttribute,
            null
          );
        }
      }

      gl.readPixels(
        0,
        0,
        _indexMap.width,
        _indexMap.height,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        this.index_buffer
      );

      gl.disableVertexAttribArray(viewer._programConstantColor.vertexAttribute);
    };

    this.runPickingVbos = function (nodes) {
      gl.useProgram(viewer._programConstantColor);

      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        _indexMap,
        0
      );

      gl.enableVertexAttribArray(viewer._programConstantColor.vertexAttribute);

      gl.clearColor(0.0, 0.0, 0.0, 0.0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.colorMask(true, true, true, true);

      var pMatrix = viewer.pMatrix
        .scale(1.0, 1.0, 0.5)
        .translate(0.0, 0.0, 0.5);

      gl.uniformMatrix4fv(
        viewer._programConstantColor.projectionMatrix,
        false,
        pMatrix.toFloat32Array()
      );

      var vbosId = 1;
      vbosMap = new Array();
      for (var n = 0; n < nodes.length; n++) {
        var node = nodes[n];

        if (typeof node == "undefined") {
          continue;
        }

        if (
          typeof viewer.cursorInstance != "undefined" &&
          node.instance.name == viewer.cursorInstance.instance.name
        ) {
          continue;
        }

        if (!(node.drawmode & Sculpteo3DViewerCommon.DRAWMODE_FLAT)) {
          continue;
        }

        var vbos = node.model;

        if (!vbos) {
          continue;
        }

        var mvMatrix = viewer.mvMatrix.x(node.transform);

        gl.uniformMatrix4fv(
          viewer._programConstantColor.modelViewMatrix,
          false,
          mvMatrix.toFloat32Array()
        );

        for (var i = 0; i < vbos.length; i++) {
          var vbi = [vbos[i]];
          vbi.vbosId = vbosId;
          vbosMap[vbosId] = vbi[0];
          var rgbaNode = packRGBA8(vbosId); // Convert the node's index to an RGBA colour

          gl.uniform4f(
            viewer._programConstantColor.color,
            rgbaNode[0] / 255,
            rgbaNode[1] / 255,
            rgbaNode[2] / 255,
            rgbaNode[3] / 255
          );

          viewer.drawModelWithPositionsAndNormals(
            vbi,
            viewer._programConstantColor.vertexAttribute,
            null
          );
          vbosId += 1;
        }
      }
      gl.readPixels(
        0,
        0,
        _indexMap.width,
        _indexMap.height,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        this.index_buffer
      );

      gl.disableVertexAttribArray(viewer._programConstantColor.vertexAttribute);
      return vbosMap;
    };

    // Run the picking process for a CSG viewer.
    //
    // You need to call that function to update the picking so that you can get the index at a given
    // location. See the function 'indexAt' for further details.
    //
    // Notice: if you want to perform picking with CSG renders, please use the 'runPickingCSG'
    // function.
    this.runPickingCSG = function (nodes) {
      var program = viewer._programConstantColorCSGTest;

      gl.useProgram(program);

      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        _indexMap,
        0
      );

      gl.clearColor(0.0, 0.0, 1.0, 1.0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.colorMask(true, true, true, true);

      var pMatrix = viewer.pMatrix;

      gl.uniformMatrix4fv(
        program.projectionMatrix,
        false,
        pMatrix.toFloat32Array()
      );

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(
        gl.TEXTURE_2D,
        viewer.pingPongBuffer[1 - viewer.PPCurrent]
      );

      gl.uniform1i(program.csgMap, 0);
      gl.uniform2f(
        program.viewportScale,
        gl.viewportWidth / nearestUpperPow2(gl.viewportWidth),
        gl.viewportHeight / nearestUpperPow2(gl.viewportHeight)
      );

      gl.enableVertexAttribArray(program.vertexAttribute);

      for (var n = 0; n < nodes.length; n++) {
        var node = nodes[n];

        if (typeof node == "undefined") {
          continue;
        }

        if (!(node.drawmode & Sculpteo3DViewerCommon.DRAWMODE_FLAT)) {
          continue;
        }

        var vbos = node.model;

        if (!vbos) {
          continue;
        }

        var is_cursor =
          typeof viewer.cursorInstance != "undefined" &&
          node == viewer.cursorInstance;

        if (is_cursor) {
          continue;
        }

        if (node.opacity != 1.0) {
          continue;
        }

        var mvMatrix = viewer.mvMatrix.x(node.transform);

        gl.uniformMatrix4fv(
          program.modelViewMatrix,
          false,
          mvMatrix.toFloat32Array()
        );

        gl.uniform4f(program.color, 0.0, 0.0, 0.0, n / 255.0);

        // Use proper culling
        if (node.complemented) {
          gl.cullFace(gl.FRONT);
        } else {
          gl.cullFace(gl.BACK);
        }

        viewer.drawModelWithPositionsAndNormals(
          vbos,
          program.vertexAttribute,
          null
        );
      }

      gl.readPixels(
        0,
        0,
        _indexMap.width,
        _indexMap.height,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        this.index_buffer
      );

      gl.disableVertexAttribArray(program.vertexAttribute);

      gl.cullFace(gl.BACK);
    };

    // Get the index at a given position in the index buffer.
    this.indexAt = function (x, y) {
      var pixels = this.index_buffer;
      var baseIndex = 4 * (y * _indexMap.width + x);

      return unpackRGBA8([
        pixels[baseIndex],
        pixels[baseIndex + 1],
        pixels[baseIndex + 2],
        pixels[baseIndex + 3],
      ]);
    };
  }; // Picking type

  return module;
})(Sculpteo || {}); // Module
